import React, { useEffect, useState } from "react";
import { useDispatch} from "react-redux";
import {
    createOrder,
    createOrderManual,
    fetchOrderById,
    fetchOrders,
    updateOrderStatus
} from "../../../../../reduxLib/ordersLib";
import EditModal from "./elements/editModal";
import ViewModal from "./elements/viewModal";
import ManualOrderModal from "./elements/manualOrderModal";
import {
    addItemToShoppingCard,
    removeItemFromShoppingCard,
    UpdateItemToShoppingCard
} from "../../../../../reduxLib/shoppingcardLib";


function OrdersAdmin() {

    const dispatch = useDispatch();
    const [ordersPage, setOrdersPage] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [statusFilter, setStatusFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [isManualOrderOpen, setIsManualOrderOpen] = useState(false);

    const [isViewOpen, setIsViewOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [isEditOpen, setIsEditOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchOrders()).then(async (action) => {
            if (!action.payload) return;

            const orders = Array.isArray(action.payload) ? action.payload : [];

            const ordersWithDetails = await Promise.all(
                orders.map(async (order) => {
                    const orderAction = await dispatch(
                        fetchOrderById(order.orderId)
                    );

                    return {
                        ...order,
                        orderOwnerName:
                            orderAction.payload?.orderOwnerName || "N/A",

                        orderOwnerEmail:
                            orderAction.payload?.orderOwnerEmail || "N/A",

                        totalPrice:
                            orderAction.payload?.totalPrice ?? order.totalPrice ?? 0,
                    };
                })
            );

            setOrdersPage(ordersWithDetails);
        });
    }, [dispatch]);

    const pendingCount = ordersPage.filter(
        (order) => order.orderStatus === "placed"
    ).length;

    const processingCount = ordersPage.filter(
        (order) => order.orderStatus === "pending"
    ).length;

    const shippedCount = ordersPage.filter(
        (order) => order.shipped === true
    ).length;
    const cancelledCount = ordersPage.filter(
        (order) => order.orderStatus === "cancelled"
    ).length;

    const filteredOrders = ordersPage.filter((order) => {
        // STATUS FILTER
        const matchesStatus =
            statusFilter === "all" ||
            order.orderStatus === statusFilter;

        // DATE FILTER
        const now = new Date();

        const orderDate = order.dateCreated
            ? new Date(order.dateCreated)
            : null;

        let matchesDate = true;

        if (orderDate) {
            switch (dateFilter) {
                case "today":
                    matchesDate =
                        orderDate.toDateString() === now.toDateString();
                    break;

                case "7days": {
                    const last7Days = new Date();
                    last7Days.setDate(now.getDate() - 7);

                    matchesDate = orderDate >= last7Days;
                    break;
                }

                case "30days": {
                    const last30Days = new Date();
                    last30Days.setDate(now.getDate() - 30);

                    matchesDate = orderDate >= last30Days;
                    break;
                }

                case "month":
                    matchesDate =
                        orderDate.getMonth() === now.getMonth() &&
                        orderDate.getFullYear() === now.getFullYear();
                    break;

                case "custom": {
                    const fromDate = dateFrom
                        ? new Date(dateFrom)
                        : null;

                    const toDate = dateTo
                        ? new Date(`${dateTo}T23:59:59`)
                        : null;

                    matchesDate =
                        (!fromDate || orderDate >= fromDate) &&
                        (!toDate || orderDate <= toDate);

                    break;
                }

                default:
                    matchesDate = true;
            }
        }

        return matchesStatus && matchesDate;
    });

// reset pagination on filters
    useEffect(() => {
        setCurrentPage(1);
    }, [
        statusFilter,
        dateFilter,
        dateFrom,
        dateTo
    ]);

// pagination
    const totalOrders = filteredOrders.length;
    const totalPages = Math.ceil(totalOrders / itemsPerPage);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const paginatedOrders = filteredOrders.slice(
        startIndex,
        endIndex
    );

    return (
        <div id="orders-content" className="p-8 bg-cream font-script">
            <div id="orders-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Orders</p>
                            <p className="text-2xl font-bold text-gray-800">
                                {ordersPage.length}
                            </p>
                        </div>
                        <i className="fa-solid fa-shopping-cart text-2xl text-custom-green"></i>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Pending</p>
                            <p className="text-2xl font-bold text-yellow-600">
                                {pendingCount}
                            </p>
                        </div>
                        <i className="fa-solid fa-clock text-2xl text-yellow-600"></i>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Processing</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {processingCount}
                            </p>
                        </div>
                        <i className="fa-solid fa-cog text-2xl text-blue-600"></i>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Shipped</p>
                            <p className="text-2xl font-bold text-green-600">
                                {shippedCount}
                            </p>
                        </div>
                        <i className="fa-solid fa-truck text-2xl text-green-600"></i>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow card-hover">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Cancelled</p>
                            <p className="text-2xl font-bold text-red-600">
                                {cancelledCount}
                            </p>
                        </div>
                        <i className="fa-solid fa-times-circle text-2xl text-red-600"></i>
                    </div>
                </div>
            </div>

            <div id="orders-controls" className="bg-white rounded-lg shadow p-6 mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                        <div className="relative">
                            <i className="fa-solid fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                            <input type="text" placeholder="Search orders..."
                                   className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent w-full sm:w-64"/>
                        </div>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                        >
                            <option value="all">All Status</option>
                            <option value="placed">Placed</option>
                            <option value="pending">Processing</option>
                            <option value="paid">Paid</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>

                        <select
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                            >
                        <option value="30days">Last 30 Days</option>
                        <option value="7days">Last 7 Days</option>
                        <option value="today">Today</option>
                        <option value="month">This Month</option>
                        <option value="all">All Time</option>
                            <option value="custom">Custom Range</option>
                    </select>
                        {dateFilter === "custom" && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                                />

                                <span className="text-gray-500 text-sm">
            to
        </span>

                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex space-x-3">
                        <button
                            onClick={() => setIsManualOrderOpen(true)}
                            className="px-4 py-2 bg-custom-green text-white rounded-lg hover:bg-opacity-90 flex items-center">
                            <i className="fa-solid fa-plus mr-2"></i>
                            Manual Order
                        </button>
                    </div>
                </div>
            </div>

            <div id="orders-table" className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Num of Products</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedOrders.map((order) => (
                            <tr key={order.orderId} className="order-row">
                                <td className="px-6 py-4 whitespace-nowrap text-left">
                                    <div>
                                        <div className="font-medium text-gray-900">
                                            #{order.orderId}
                                        </div>
                                        {/* <div className="text-sm text-gray-500">Payment: Stripe</div> */}
                                    </div>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-left">
                                    <div>
                                        <div className="font-medium text-gray-900">
                                            {order.orderOwnerName || "N/A"}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {order.orderOwnerEmail || "N/A"}
                                        </div>
                                    </div>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-left">
                                    <div className="text-sm text-gray-900">
                                        {order.dateCreated
                                            ? new Date(order.dateCreated).toLocaleDateString("el-GR")
                                            : "-"}
                                    </div>

                                    <div className="text-sm text-gray-500">
                                        {order.dateCreated
                                            ? new Date(order.dateCreated).toLocaleTimeString("el-GR", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })
                                            : ""}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-left">
                                    <div className="text-sm text-gray-900">
                                        {order.numProducts ?? 0}
                                    </div>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-left">
    <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            order.orderStatus === "pending"
                ? "bg-orange-100 text-orange-700"
                : order.orderStatus === "placed"
                    ? "bg-blue-100 text-blue-700"
                    : order.orderStatus === "paid"
                        ? "bg-emerald-100 text-emerald-700"
                        : order.orderStatus === "completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
        }`}
    >
        {order.orderStatus || "-"}
    </span>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-left">
                                    <div className="font-medium text-gray-900">
                                        €{Number(order.total || 0).toFixed(2)}
                                    </div>
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-left">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => {
                                                setSelectedOrder(order);
                                                setIsViewOpen(true);
                                            }}
                                            className="text-custom-green hover:text-opacity-80">
                                            <i className="fa-solid fa-eye"></i>
                                        </button>
                                        <button

                                            onClick={() => {
                                                setSelectedOrder(order);
                                                setIsEditOpen(true);
                                            }}
                                            className="text-blue-600 hover:text-blue-800">
                                            <i className="fa-solid fa-edit"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                <div
                    id="pagination"
                    className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t"
                >
                    <div className="flex items-center">
        <span className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
                {totalOrders === 0 ? 0 : startIndex + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
                {Math.min(endIndex, totalOrders)}
            </span>{" "}
            of{" "}
            <span className="font-medium">
                {totalOrders}
            </span>{" "}
            orders
        </span>
                    </div>

                    <div className="flex space-x-2">
                        <button
                            onClick={() =>
                                setCurrentPage((prev) =>
                                    Math.max(prev - 1, 1)
                                )
                            }
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            Previous
                        </button>

                        {Array.from(
                            { length: totalPages },
                            (_, index) => index + 1
                        ).map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 text-sm border rounded ${
                                    currentPage === page
                                        ? "bg-custom-green text-white border-custom-green"
                                        : "bg-white border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() =>
                                setCurrentPage((prev) =>
                                    Math.min(prev + 1, totalPages)
                                )
                            }
                            disabled={
                                currentPage === totalPages ||
                                totalPages === 0
                            }
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            <ManualOrderModal
                isOpen={isManualOrderOpen}
                onClose={() => setIsManualOrderOpen(false)}
                onSave={ async (manualOrder) => {
                    console.log("MANUAL ORDER", manualOrder);
                    try {
                        const response = await dispatch(createOrderManual(manualOrder)).unwrap();

                        console.log("CREATE ORDER RESPONSE", response);

                        await dispatch(fetchOrders());
                        setIsManualOrderOpen(false);
                    } catch (error) {
                        console.error(error);
                    }
                }}
            />

            <ViewModal
                isOpen={isViewOpen}
                onClose={() => {
                    setIsViewOpen(false);
                    setSelectedOrder(null);
                }}
                order={selectedOrder}
            />

            <EditModal
                isOpen={isEditOpen}
                onClose={() => {
                    setIsEditOpen(false);
                    setSelectedOrder(null);
                }}
                order={selectedOrder}
                onSave={async ({ shoppingCardId,orderStatus, added, updated, removed }) => {
                    //console.log("SAVE ", item);
                    //console.log("Update",update.item)
                    try {
                        for (const cardItemId of removed) {
                            await dispatch(removeItemFromShoppingCard(cardItemId)).unwrap();
                        }

                        for (const item of added) {
                            await dispatch(addItemToShoppingCard({
                                cardId: shoppingCardId,
                                item,
                            })).unwrap();
                        }

                        for (const update of updated) {
                            await dispatch(UpdateItemToShoppingCard({
                                cardId: shoppingCardId,
                                itemId: update.cardItemId,
                                item: update.item,
                            })).unwrap();
                        }
                        //console.log("SAVE orderStatus ", orderStatus);
                        //console.log("SAVE selectedOrder.orderStatus ", selectedOrder.orderStatus);
                        if (orderStatus !== selectedOrder.orderStatus) {
                            await dispatch(
                                updateOrderStatus({
                                    id: selectedOrder.orderId,
                                    orderData: orderStatus,
                                })
                            ).unwrap();
                        }

                        await dispatch(fetchOrders()).unwrap();

                        setIsEditOpen(false);
                        setSelectedOrder(null);
                    } catch (error) {
                        console.error("SAVE CARD CHANGES ERROR", error);
                    }
                }
            }
            />
        </div>
    );

}

export default OrdersAdmin;